"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PaymentRepository = void 0;
const zod_1 = require("zod");
const setTime_1 = require("../utils/setTime");
const httpRepository_1 = __importDefault(require("./httpRepository"));
const userRepository_1 = __importDefault(require("./userRepository"));
const BillingAddressSchema = zod_1.z.object({
    address: zod_1.z.union([zod_1.z.null(), zod_1.z.string()]).optional(),
    addressLine1: zod_1.z.union([zod_1.z.null(), zod_1.z.string()]).optional(), // on get card for wallet
    city: zod_1.z.union([zod_1.z.null(), zod_1.z.string()]).optional(),
    countryCode: zod_1.z.union([zod_1.z.null(), zod_1.z.string()]).optional(),
    regionCode: zod_1.z.union([zod_1.z.null(), zod_1.z.string()]).optional(),
    region: zod_1.z.union([zod_1.z.null(), zod_1.z.string()]).optional(), // on get card for wallet
    zipCode: zod_1.z.union([zod_1.z.null(), zod_1.z.string()]).optional(),
});
const InfoSchema = zod_1.z.object({
    balance: zod_1.z.union([zod_1.z.number(), zod_1.z.null()]).optional(),
    billingAddress: zod_1.z.union([BillingAddressSchema, zod_1.z.null()]).optional(),
    cardType: zod_1.z.union([zod_1.z.null(), zod_1.z.string()]).optional(),
    currency: zod_1.z.union([zod_1.z.null(), zod_1.z.string()]).optional(),
    id: zod_1.z.union([zod_1.z.null(), zod_1.z.string()]).optional(),
    provider: zod_1.z.union([zod_1.z.null(), zod_1.z.string()]).optional(),
    validEndingOn: zod_1.z.union([zod_1.z.null(), zod_1.z.string()]).optional(),
    value: zod_1.z.union([zod_1.z.number(), zod_1.z.null()]).optional(),
    owner: zod_1.z.union([zod_1.z.null(), zod_1.z.string()]).optional(),
    sourceId: zod_1.z.union([zod_1.z.null(), zod_1.z.string()]).optional(),
    status: zod_1.z.union([zod_1.z.null(), zod_1.z.string()]).optional(),
    frequency: zod_1.z.union([zod_1.z.null(), zod_1.z.string()]).optional(),
    createdAt: zod_1.z.union([zod_1.z.null(), zod_1.z.string()]).optional(),
    cardNumber: zod_1.z.union([zod_1.z.null(), zod_1.z.string()]).optional(),
});
const _VirtualCardData = zod_1.z.object({
    cvv: zod_1.z.union([zod_1.z.null(), zod_1.z.string()]).optional(),
    expiry: zod_1.z.union([zod_1.z.null(), zod_1.z.string()]).optional(),
    expirationDate: zod_1.z.union([zod_1.z.null(), zod_1.z.string()]).optional(),
    billingAddress: zod_1.z.union([BillingAddressSchema, zod_1.z.null()]).optional(),
    info: zod_1.z.union([InfoSchema, zod_1.z.null()]).optional(),
    number: zod_1.z.union([zod_1.z.null(), zod_1.z.string()]).optional(),
    provider: zod_1.z.union([zod_1.z.null(), zod_1.z.string()]).optional(),
    cardType: zod_1.z.union([zod_1.z.null(), zod_1.z.string()]).optional(),
    cardStatus: zod_1.z.union([zod_1.z.null(), zod_1.z.string()]).optional(),
    status: zod_1.z.union([zod_1.z.null(), zod_1.z.string()]).optional(),
    lastDigits: zod_1.z.union([zod_1.z.null(), zod_1.z.string()]).optional(),
    missingReceipts: zod_1.z.union([zod_1.z.null(), zod_1.z.number()]).optional(),
    balance: zod_1.z.union([zod_1.z.number(), zod_1.z.null()]).optional(),
});
exports.PaymentRepository = {
    getCardSources: async (params, teamId) => {
        const response = teamId
            ? await httpRepository_1.default.tradeshiftGo().get(`/rest/teams/${teamId}/cardsources`, params)
            : await httpRepository_1.default.tradeshiftGo().get('/rest/payments/cardsources', params);
        return response.data;
    },
    setCardName: async ({ cardSourceId, name, teamId }) => {
        const response = teamId == null
            ? await httpRepository_1.default.tradeshiftGo().put(`/rest/payments/cardsources/${cardSourceId}/name`, { name })
            : await httpRepository_1.default.tradeshiftGo().put(`/rest/teams/${teamId}/cardsources/${cardSourceId}/name`, { name });
        return response.data;
    },
    addCardSourceToTeam: async ({ teamId, cardSourceId }) => {
        const response = await httpRepository_1.default.tradeshiftGo().put(`/rest/teams/${teamId}/cardsources/${cardSourceId}`);
        return response.data;
    },
    removeCardSourceFromTeam: async ({ teamId, cardSourceId }) => {
        const response = await httpRepository_1.default.tradeshiftGo().delete(`/rest/teams/${teamId}/cardsources/${cardSourceId}`);
        return response.data;
    },
    getEnrollmentToken: async ({ teamId }) => {
        const response = teamId
            ? await httpRepository_1.default.tradeshiftGo().get(`/rest/teams/${teamId}/cardsources/enrollment`)
            : await httpRepository_1.default.tradeshiftGo().get('/rest/payments/enrollment');
        return response.data;
    },
    enrollWithAmex: async (options) => {
        const response = await httpRepository_1.default.payments(options.enrollmentToken).post('/amex/enrollment', options.paymentInformation);
        return response.data;
    },
    enrollWithHsbc: async (options) => {
        const response = await httpRepository_1.default.fintechCards(options.enrollmentToken).post('/cardsources', options.paymentInformation);
        return response.data;
    },
    getTeamApprovers: async ({ excludeSelf, excludeOutOfOffice, teamId }) => {
        const { data: approvers } = await httpRepository_1.default.tradeshiftGo().get(`/rest/teams/${teamId}/approvers`, {
            params: {
                excludeOutOfOffice,
            },
        });
        if (excludeSelf) {
            const user = await userRepository_1.default.getUser();
            return approvers.users.filter(({ userId }) => userId !== user?.userId);
        }
        return approvers.users;
    },
    getCompanyCardSources: async (params) => {
        const response = await httpRepository_1.default.tradeshiftGo().get(`/rest/payments/cardsources?includeTeamsCount=${params.includeTeamsCount}`);
        return response.data;
    },
    getTeamCardSources: async (params) => {
        if (params.teamId) {
            const response = await httpRepository_1.default.tradeshiftGo().get(`/rest/teams/${params.teamId}/cardsources?includeTeamsCount=${params.includeTeamsCount}`);
            return response.data;
        }
        else {
            return [];
        }
    },
    getTeamsForCardSource: async (cardSourceId) => {
        const response = await httpRepository_1.default.tradeshiftGo().get(`/rest/payments/cardsources/${cardSourceId}/teams`);
        return response.data;
    },
    getCardSource: async ({ cardSourceId, includeCardHolder, teamId }) => {
        const response = teamId
            ? await httpRepository_1.default.tradeshiftGo().get(`/rest/teams/${teamId}/cardsources/${cardSourceId}`, {
                params: {
                    includeCardHolder: !!includeCardHolder,
                },
            })
            : await httpRepository_1.default.tradeshiftGo().get(`/rest/payments/cardsources/${cardSourceId}`, {
                params: {
                    includeCardHolder: !!includeCardHolder,
                },
            });
        return response.data;
    },
    getCurrencies: async () => {
        const response = await httpRepository_1.default.tradeshiftGo().get('/rest/payments/currencies');
        return response.data;
    },
    getVirtualCardToken: async (documentId) => {
        const response = await httpRepository_1.default.tradeshiftGo().get(`/rest/payments/card?documentId=${documentId}`);
        return response.data;
    },
    getVirtualCard: async (token, cardProvider) => {
        if (!cardProvider) {
            throw new TypeError('cardProvider is required');
        }
        const response = cardProvider === 'HSBC'
            ? await httpRepository_1.default.fintechCards(token).get('/card', {
                timeout: (0, setTime_1.setTime)(10, 'seconds'),
                headers: { 'Cache-Control': 'no-cache' },
            })
            : await httpRepository_1.default.payments(token).get('/card', { timeout: (0, setTime_1.setTime)(10, 'seconds') });
        return response.data;
    },
    getVirtualCardInfo: async (options) => {
        if (!options.documentId) {
            throw new Error('documentId is required');
        }
        const response = await httpRepository_1.default.tradeshiftGo().get(`/rest/payments/card/info?documentId=${options.documentId}`);
        return response.data;
    },
};
